import { FC, PropsWithChildren, useCallback, useEffect } from 'react'
import axios from 'axios'
import ToastProvider from '@/components/Toast/ToastProvider'
import TooltipProvider from '@/components/Tooltip/TooltipProvider'
import { useEnv } from '@/hooks/useEnv'
import { useProfile } from '@/hooks/useProfile'
import { useAuth } from '@/hooks/useAuth'
import useTrackerSetup from '@/hooks/useTrackerSetup'

const client = axios.create()

interface Props {
  authRequired?: boolean
}

const SiteLayout: FC<PropsWithChildren<Props>> = ({ authRequired, children }) => {
  const env = useEnv()
  const { profile, refresh } = useProfile()
  const { setIsAuthenticated } = useAuth()

  useTrackerSetup()

  const loginWithToken = useCallback(
    (accessToken: string) => {
      const tryLogin = async () => {
        const { data } = await client.post<{ success: boolean }>(
          '/api/auth/line/liff',
          { accessToken },
          { withCredentials: true }
        )

        if (data?.success) {
          refresh()
        }
      }

      tryLogin()
    },
    [refresh]
  )

  // auto login
  useEffect(() => {
    // if auth has been checked, it'll become object or null
    if (typeof profile === 'undefined') return

    const isAuthenticated = !!profile
    setIsAuthenticated(isAuthenticated)

    // don't continue before liffEnv is determined
    if (!env.isLiffReady) return

    const authNeeded = !isAuthenticated && authRequired

    try {
      if (liff.isLoggedIn() && authNeeded) {
        loginWithToken(liff.getAccessToken())
        return
      }
    } catch (e) {
      // in case liff does not work
      console.log('error while getting accessToken from liff')
    }

    // Force login in Web
    if (authNeeded) { window.location.href = '/api/auth/line/login' }
  }, [profile, env, authRequired])

  useEffect(() => {
    if (!env.isLiffReady) return
    refresh()
  }, [env.isLiffReady])

  return (
    <>
      {children}
      <ToastProvider />
      <TooltipProvider />
    </>
  )
}

SiteLayout.defaultProps = {
  authRequired: false
}

export default SiteLayout
